import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useUser } from '../UserContext';
import { getLesson, canAccessLesson } from '../data/lessons';
import { BookOpen, Home, ChevronRight, ChevronLeft, ChevronDown, Star, Heart, Check, Volume2, Eye, CheckCircle, Trophy, Lock, Zap, Play, Calendar, Award, X, ArrowLeft, ArrowRight, MessageSquare, Send, Baby, Clock, Shield } from 'lucide-react';
import { trackEvent, trackPageView } from '../utils/analyticsTracker';
import { useNavMeta } from '../utils/usePageView';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import StepCard from '../components/StepCard';
import LessonBanner from '../components/LessonBanner';

const AGE_GROUPS = [
  { phase: 1, label: '1-2', subtitle: 'Toddler basics', color: 'from-pink-400 to-rose-500', icon: '/assets/P1F.webp',
    completionLine: 'That animal sounds activity just introduced sound-meaning connections, the foundation of reading.' },
  { phase: 2, label: '2-3', subtitle: 'Building words', color: 'from-green-400 to-teal-500', icon: '/assets/P2F.webp',
    completionLine: 'That naming activity just built vocabulary foundations, the building blocks of reading.' },
  { phase: 3, label: '3-4', subtitle: 'Early sentences', color: 'from-blue-400 to-indigo-500', icon: '/assets/P3F.webp',
    completionLine: 'That letter activity just introduced sound-letter connections, the key to unlocking reading.' },
  { phase: 4, label: '4-5', subtitle: 'Reading readiness', color: 'from-indigo-400 to-purple-500', icon: '/assets/P4F.webp',
    completionLine: 'That blending activity just practiced sounding out real words, your child is reading!' },
];

const LessonPage = ({ freeTrialMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lessonId } = useParams();
  const [searchParams] = useSearchParams();
  const userContext = useUser();
  // In free trial mode, ignore the user context entirely
  const user = freeTrialMode ? null : userContext.user;
  const userData = freeTrialMode ? null : userContext.userData;
  const completeLesson = freeTrialMode ? null : userContext.completeLesson;
  const submitReview = freeTrialMode ? null : userContext.submitReview;
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isLessonCompleted, setIsLessonCompleted] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [lessonError, setLessonError] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showWeekView, setShowWeekView] = useState(false);
  const [viewingWeek, setViewingWeek] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [showReferences, setShowReferences] = useState(false);
  const [showWeeklyRating, setShowWeeklyRating] = useState(false);
  const [weeklyRatingValue, setWeeklyRatingValue] = useState(0);
  const [weeklyRatingHover, setWeeklyRatingHover] = useState(0);
  const [weeklyRatingNote, setWeeklyRatingNote] = useState('');
  const [weeklyRatingSubmitting, setWeeklyRatingSubmitting] = useState(false);
  const [weeklyRatingSubmitted, setWeeklyRatingSubmitted] = useState(false);
  const [loadingAgeCard, setLoadingAgeCard] = useState(null); // phase number of card being loaded
  const [screenFading, setScreenFading] = useState(false); // fade between view transitions
  // Check URL params for pre-selected phase (from homepage age selector),
  // falling back to sessionStorage so the selection survives Safari tab
  // restoration / bfcache remounts.
  const preSelectedPhase = searchParams.get('phase') ? parseInt(searchParams.get('phase')) : null;
  const storedPhase = freeTrialMode && !preSelectedPhase
    ? (() => { try { const v = sessionStorage.getItem('trialAgeGroup'); return v ? parseInt(v) : null; } catch { return null; } })()
    : null;
  const [trialAgeGroup, setTrialAgeGroup] = useState(preSelectedPhase || storedPhase); // null = show selector, 1-4 = selected phase
  // Context intro shown ONCE for cold ad landers before the age selector.
  // Skipped if:
  //   - a phase was pre-selected (came from a warmer surface),
  //   - already passed it in this session,
  //   - the visitor arrived via an internal CTA that explicitly set
  //     `state.skipIntro` (e.g. the homepage "Try free activity" button),
  //   - the visitor reached /free-activity via in-app SPA navigation
  //     rather than a direct page load (React Router v6 marks the initial
  //     page-load location with key === 'default' and assigns a unique key
  //     to every subsequent navigate() call),
  //   - OR the visitor has a recent localStorage stamp from any other page
  //     on the site (set by <WarmVisitTracker> in App.js). This catches the
  //     cases the location.key check misses: opening /free-activity in a
  //     NEW TAB from home (mobile long-press), refreshing on /free-activity
  //     after visiting elsewhere, returning via a bookmark after browsing,
  //     etc. The intro is ONLY for cold direct ad lands where the user has
  //     never touched any other page on the site recently.
  // If the visitor arrived from /try (ad landing), back buttons should
  // return them to /try instead of /. navSource is passed via router state.
  const cameFromTry = (location.state?.navSource || '').startsWith('try_');
  const homeRoute = cameFromTry ? '/try' : '/';
  const skipIntroFromState = !!location.state?.skipIntro;
  const skipIntroFromInAppNav = location.key && location.key !== 'default';
  const skipIntroFromWarmVisit = (() => {
    try {
      const ts = parseInt(localStorage.getItem('br_warm_visit_ts') || '0', 10);
      // 30-min window matches the analytics session timeout — long enough
      // to cover a real browsing session, short enough that a returning
      // visitor weeks later still gets the intro.
      return ts > 0 && (Date.now() - ts) < 30 * 60 * 1000;
    } catch { return false; }
  })();
  const [trialIntroPassed, setTrialIntroPassed] = useState(() => {
    if (preSelectedPhase || storedPhase || skipIntroFromState || skipIntroFromInAppNav || skipIntroFromWarmVisit) return true;
    try { return sessionStorage.getItem('trialIntroPassed') === '1'; } catch { return false; }
  });
  const contentRef = useRef(null);

  // Persist trial age group so it survives Safari tab restoration
  useEffect(() => {
    if (freeTrialMode && trialAgeGroup) {
      try { sessionStorage.setItem('trialAgeGroup', String(trialAgeGroup)); } catch {}
    }
  }, [freeTrialMode, trialAgeGroup]);

  // In free trial mode, push a history entry when age is selected so the
  // browser back button returns to the age selector instead of leaving.
  const hasSetTrialHistory = useRef(false);
  useEffect(() => {
    if (!freeTrialMode || !trialAgeGroup) {
      hasSetTrialHistory.current = false;
      return;
    }
    if (hasSetTrialHistory.current) return; // only push once
    hasSetTrialHistory.current = true;
    window.history.pushState({ trialAge: trialAgeGroup }, '');
    const onPopState = (e) => {
      // Only handle our own history entry — ignore React Router navigation
      if (!e.state || !e.state.trialAge) {
        try { sessionStorage.removeItem('trialAgeGroup'); } catch {}
        setTrialAgeGroup(null);
        setLoadingAgeCard(null);
        setScreenFading(false);
        setIsLessonCompleted(false);
        setCurrentStep(0);
        setCompletedSteps([]);
        setLessonError(false);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [freeTrialMode, trialAgeGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentLessonNum = freeTrialMode ? 1 : (parseInt(lessonId) || 1);
  const isPremium = userData?.subscription === 'paid';

  // Entry-time nav context — captured how the user ENTERED this LessonPage
  // instance (cold ad land, in-app click, browser back, etc.). Used as-is
  // for the very first screen (intro for cold landers, age picker for warm
  // visitors). Internal screen transitions (intro → age picker → lesson)
  // override navSource to describe the transition itself, while keeping
  // the rest of the entry context for funnel attribution.
  const navMeta = useNavMeta();

  // Track free-activity page views. Each screen owns exactly ONE event tag —
  // no overlap, no umbrella events. Visitors only count for a screen if they
  // actually reach it.
  //   - 'free-activity-intro'  = cold-lander context screen
  //   - 'free-activity'        = age picker
  //   - 'free-activity-lesson' = the activity itself (after they pick an age)
  useEffect(() => {
    if (!freeTrialMode) return;
    if (!trialAgeGroup && !trialIntroPassed) {
      // Entry into the intro screen — propagate the entry nav context.
      trackPageView('free-activity-intro', null, navMeta);
    } else if (!trialAgeGroup && trialIntroPassed) {
      // Internal transition: user clicked Continue on the intro.
      trackPageView('free-activity', null, { ...navMeta, navSource: 'trial_intro_continue' });
    } else if (trialAgeGroup) {
      // Internal transition: user selected an age on the picker.
      trackPageView('free-activity-lesson', null, { ...navMeta, navSource: `trial_age_select_${trialAgeGroup}` });
    }
  }, [freeTrialMode, trialIntroPassed, trialAgeGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // In free trial mode, only track lesson start after age group is selected
    if (freeTrialMode && !trialAgeGroup) return;
    if (!freeTrialMode) {
      trackPageView('lesson', userData?.email || null, navMeta);
    }
    trackEvent('lesson_start', { lessonId: currentLessonNum, phase: freeTrialMode ? trialAgeGroup : (userData?.currentPhase || 1), ...(freeTrialMode && { ageGroup: AGE_GROUPS.find(a => a.phase === trialAgeGroup)?.label }) }, userData?.email || null);
  }, [currentLessonNum, trialAgeGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase-specific data — in free trial mode, use the selected age group phase
  const currentPhase = freeTrialMode ? (trialAgeGroup || 1) : (userData?.currentPhase || 1);
  const phaseData = userData?.phaseProgress?.[currentPhase] || { completedLessons: [], lessonCompletionDates: {} };
  const completedLessons = phaseData.completedLessons || [];
  const completedLessonsCount = completedLessons.length;
  
  
  // Calculate total completed lessons across ALL phases for free users
  const totalCompletedAcrossAllPhases = !isPremium 
    ? Object.values(userData?.phaseProgress || {}).reduce((total, phase) => {
        return total + (phase.completedLessons?.length || 0);
      }, 0)
    : 0;
  
  const isFreeTrial = currentLessonNum <= 10;
  const hasFreeLessonsRemaining = !isPremium && totalCompletedAcrossAllPhases < 10;
  const userCanAccess = isPremium || (isFreeTrial && (hasFreeLessonsRemaining || alreadyCompleted));

  const lesson = getLesson(currentLessonNum, currentPhase);
  const alreadyCompleted = completedLessons.includes(currentLessonNum);

  // Lock body scroll when any modal is open (prevents iOS Safari background scroll)
  const anyModalOpen = showCompletionModal || showPaywall || showWeekView || showReviewPrompt || showWeeklyRating;
  useEffect(() => {
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [anyModalOpen]);

  useEffect(() => {
    if (alreadyCompleted) {
      setIsLessonCompleted(true);
      // Don't reset step/scroll when completion just happened — a modal is showing
      return;
    }

    if (!lesson) {
      setLessonError(true);
    }

    setCurrentStep(0);
    setViewingWeek(null);
    setShowReferences(false);
    window.scrollTo({ top: 0, behavior: 'auto' });

    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 50);

    return () => clearTimeout(timer);
  }, [lessonId, alreadyCompleted, lesson]);

  const rawStepData = lesson?.steps?.[currentStep];
  const progressPercent = lesson ? ((currentStep + 1) / lesson.steps.length) * 100 : 0;
  const isV2 = lesson?.version === 2;

  // Adapter: convert V1 step data into the shape StepCard expects.
  // V2 lessons already have the right shape and pass through unchanged.
  const currentStepData = React.useMemo(() => {
    if (!rawStepData) return null;
    if (isV2) return rawStepData;

    const c = rawStepData.content || {};
    const type = rawStepData.type;

    // Conclusion steps get their own dedicated layout — don't force into Say/Do
    if (type === 'conclusion') {
      // Gather all activity step names from the lesson for the summary
      const activitySteps = (lesson?.steps || [])
        .filter(s => s.type === 'activity' || (s.name && !s.type))
        .map(s => s.title || s.name);

      return {
        ...rawStepData,
        stepNumber: rawStepData.id != null ? rawStepData.id + 1 : currentStep + 1,
        name: '🎉 Activity Complete!',
        hook: null,
        sayThis: null,
        thenDoThis: null,
        watchFor: null,
        whyThisWorks: c.parentTakeaway ? `${c.parentTakeaway.title}: ${c.parentTakeaway.content}` : null,
        duration: rawStepData.duration,
        // Conclusion-specific fields for StepCard
        celebration: c.celebration || [],
        reviewQuestions: c.reviewQuestions || [],
        nextSteps: c.nextSteps || [],
        activitySteps,
      };
    }

    // Intro steps get their own structured layout — not crammed into Say/Do
    if (type === 'intro') {
      return {
        ...rawStepData,
        stepNumber: rawStepData.id != null ? rawStepData.id + 1 : currentStep + 1,
        name: '📚 ' + (rawStepData.title || 'Introduction'),
        hook: c.goal || null,
        sayThis: null,
        thenDoThis: null,
        watchFor: null,
        whyThisWorks: c.researchNote || null,
        duration: rawStepData.duration,
        // Intro-specific fields for StepCard
        materials: c.materials || null,
        parentGuide: c.parentGuide?.filter(l => l && l.trim()) || null,
        introTip: c.tip || null,
      };
    }

    // Build sayThis — only actual dialogue (quoted lines from whatToSay)
    const sayThis = [];
    if (type === 'activity') {
      if (c.whatToSay) sayThis.push(...c.whatToSay.filter(l => l && l.trim()));
    }
    // examples are contextual notes, not dialogue — they go into thenDoThis

    // Build thenDoThis from action items, filtering out empty strings
    const thenDoThis = [];
    if (type === 'activity') {
      if (c.whatToDo) thenDoThis.push(...c.whatToDo.filter(l => l && l.trim()));
      if (c.examples && Array.isArray(c.examples)) c.examples.forEach(ex => { if (ex && ex.trim()) thenDoThis.push(ex); });
    }

    // Build watchFor as a single string
    let watchFor = null;
    if (c.watchFor && Array.isArray(c.watchFor)) {
      watchFor = c.watchFor.join(' ');
    } else if (c.parentTip) {
      watchFor = c.parentTip;
    }

    // Build whyThisWorks from research notes
    const whyThisWorks = c.researchNote || rawStepData.researchBasis || null;

    // Build hook from goal + extras
    const hookParts = [];
    if (c.goal) hookParts.push(c.goal);
    if (c.funTwist) hookParts.push(c.funTwist);
    if (c.note) hookParts.push(c.note);

    // Step name with type prefix
    const namePrefix = type === 'intro' ? '📚 ' : '';

    return {
      ...rawStepData,
      stepNumber: rawStepData.id != null ? rawStepData.id + 1 : currentStep + 1,
      name: namePrefix + (rawStepData.title || 'Activity'),
      hook: hookParts.join(' ') || null,
      sayThis: sayThis.length > 0 ? sayThis : null,
      thenDoThis: thenDoThis.length > 0 ? thenDoThis : null,
      watchFor,
      whyThisWorks,
      duration: rawStepData.duration,
    };
  }, [rawStepData, isV2, currentStep, lesson?.steps]);

  // Build the consolidated references list shown in the intro "Research-Based" box.
  // We pull the intro's researchNote plus any researchBasis defined on activity steps,
  // so the per-step text can be removed from the lesson body without losing citations.
  const lessonReferences = React.useMemo(() => {
    if (!lesson?.steps) return [];
    const refs = [];
    lesson.steps.forEach((step) => {
      if (step.type === 'intro' && step.content?.researchNote) {
        refs.push({ citation: step.content.researchNote, usedIn: 'Intro' });
      }
      if (step.type === 'activity' && step.researchBasis) {
        refs.push({ citation: step.researchBasis, usedIn: step.title });
      }
    });
    // Allow individual lessons to override with an explicit list if they want to.
    const introStep = lesson.steps.find(s => s.type === 'intro');
    if (Array.isArray(introStep?.content?.researchReferences) && introStep.content.researchReferences.length > 0) {
      return introStep.content.researchReferences;
    }
    return refs;
  }, [lesson]);

  const handleNext = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    if (lesson && currentStep < lesson.steps.length - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'auto' });
        requestAnimationFrame(() => {
          setIsTransitioning(false);
        });
      }, 200);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        window.scrollTo({ top: 0, behavior: 'auto' });
        requestAnimationFrame(() => {
          setIsTransitioning(false);
        });
      }, 200);
    }
  };

  const handleCompleteLesson = async () => {
    if (freeTrialMode) {
      trackEvent('lesson_complete', { lessonId: 1, phase: trialAgeGroup, ageGroup: AGE_GROUPS.find(a => a.phase === trialAgeGroup)?.label, source: 'free_trial' }, null);
      setShowFeedbackForm(true);
      return;
    }

    if (!isPremium && currentLessonNum === 10) {
      setShowPaywall(true);
      return;
    }

    const result = await completeLesson(currentLessonNum);
    if (result.success) {
      if (!result.alreadyCompleted) {
        trackEvent('lesson_complete', { lessonId: currentLessonNum, phase: userData?.currentPhase || 1 }, userData?.email || null);
        setIsLessonCompleted(true);
        // End-of-week (5th lesson) → ask the parent to rate the child's week
        const isLastLessonOfWeek = ((currentLessonNum - 1) % 5) === 4;
        // Check if this was the 3rd completed activity and user hasn't reviewed yet
        const newTotal = Object.values(userData?.phaseProgress || {}).reduce((t, p) => t + (p.completedLessons?.length || 0), 0) + 1;
        if (isLastLessonOfWeek) {
          setShowWeeklyRating(true);
        } else if (newTotal === 3 && !userData?.hasSubmittedReview) {
          setShowReviewPrompt(true);
        } else {
          setShowCompletionModal(true);
        }
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleSubmitWeeklyRating = async () => {
    if (!weeklyRatingValue || !user) return;
    setWeeklyRatingSubmitting(true);
    try {
      await addDoc(collection(db, 'weekly_ratings'), {
        userId: user.uid,
        email: userData?.email || '',
        username: userData?.username || '',
        phase: currentPhase,
        week: weekNum,
        lessonId: currentLessonNum,
        rating: weeklyRatingValue,
        note: weeklyRatingNote.trim() || '',
        createdAt: new Date().toISOString()
      });
      trackEvent('weekly_rating_submit', { week: weekNum, phase: currentPhase, rating: weeklyRatingValue }, userData?.email || null);
      setWeeklyRatingSubmitted(true);
    } catch (err) {
      console.error('Error submitting weekly rating:', err);
    }
    setWeeklyRatingSubmitting(false);
  };

  const handleCloseWeeklyRating = () => {
    setShowWeeklyRating(false);
    setWeeklyRatingValue(0);
    setWeeklyRatingHover(0);
    setWeeklyRatingNote('');
    setWeeklyRatingSubmitted(false);
    setShowCompletionModal(true);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;
    setFeedbackSubmitting(true);
    try {
      await addDoc(collection(db, 'feedback'), {
        message: feedbackText.trim(),
        email: feedbackEmail.trim() || null,
        source: 'free_trial',
        ageGroup: freeTrialMode ? (AGE_GROUPS.find(a => a.phase === trialAgeGroup)?.label || null) : null,
        phase: freeTrialMode ? trialAgeGroup : null,
        createdAt: new Date().toISOString(),
        userAgent: navigator.userAgent
      });
      setFeedbackSubmitted(true);
      setFeedbackText('');
    } catch (err) {
      console.error('Error submitting feedback:', err);
    }
    setFeedbackSubmitting(false);
  };

  const handleSubmitReview = async () => {
    if (!reviewRating) return;
    setReviewSubmitting(true);
    try {
      await submitReview(reviewRating, reviewMessage);
      setReviewSubmitted(true);
    } catch (err) {
      console.error('Error submitting review:', err);
    }
    setReviewSubmitting(false);
  };

  const handleSkipReview = () => {
    setShowReviewPrompt(false);
    setShowCompletionModal(true);
  };

  const handleReviewDone = () => {
    setShowReviewPrompt(false);
    setShowCompletionModal(true);
  };

  const handleFinish = () => {
    setShowCompletionModal(false);
    navigate('/dashboard', { replace: true });
  };

  const weekNum = Math.floor((currentLessonNum - 1) / 5) + 1;
  const lessonInWeek = ((currentLessonNum - 1) % 5) + 1;
  const quarterNum = Math.ceil(weekNum / 13);

  const getWeekLessons = (week) => {
    const lessons = [];
    for (let i = 1; i <= 5; i++) {
      const lessonId = (week - 1) * 5 + i;
      const isCompleted = completedLessons.includes(lessonId);
      lessons.push({
        id: lessonId,
        number: i,
        completed: isCompleted,
        canAccess: canAccessLesson(lessonId, isPremium)
      });
    }
    return lessons;
  };

  const getQuarterWeeks = (quarter) => {
    const startWeek = (quarter - 1) * 13 + 1;
    const endWeek = Math.min(quarter * 13, 52);
    const weeks = [];
    
    for (let week = startWeek; week <= endWeek; week++) {
      const weekLessons = getWeekLessons(week);
      weeks.push({
        week,
        lessons: weekLessons,
        completed: weekLessons.every(l => l.completed),
        inProgress: weekLessons.some(l => l.completed) && !weekLessons.every(l => l.completed)
      });
    }
    return weeks;
  };

  const currentWeekLessons = getWeekLessons(weekNum);
  const currentQuarterWeeks = getQuarterWeeks(quarterNum);

  const displayWeek = showWeekView && viewingWeek !== null ? viewingWeek : weekNum;
  const displayWeekLessons = getWeekLessons(displayWeek);

  const phases = [
    { id: 1, name: 'Phase 1', subtitle: '1-2 Years', color: 'from-pink-400 to-rose-500' },
    { id: 2, name: 'Phase 2', subtitle: '2-3 Years', color: 'from-green-400 to-teal-500' },
    { id: 3, name: 'Phase 3', subtitle: '3-4 Years', color: 'from-blue-400 to-indigo-500' },
    { id: 4, name: 'Phase 4', subtitle: '4-5 Years', color: 'from-indigo-400 to-purple-500' }
  ];

  const currentPhaseInfo = phases.find(p => p.id === currentPhase);

  if (!userCanAccess) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50 animate-page-enter">
        <nav className="bg-white shadow-md p-4 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-xl">
                <BookOpen className="text-white" size={24} />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Activity Locked</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="hover:opacity-80 transition transform hover:scale-105 active:scale-95"
              >
                <img src="/assets/HomeButton.webp" alt="Home" className="h-10" />
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-4 py-8 sm:py-16">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-12 text-center">
            <div className="bg-gray-100 w-20 h-20 sm:w-32 sm:h-32 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Lock className="text-gray-400" size={40} />
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">This Activity is Locked</h2>
            <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
              You've completed your 10 free activities! 🎉<br/>
              Upgrade to unlock all 260 activities across all 4 phases and continue your child's reading journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/upgrade')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-10 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition inline-flex items-center justify-center gap-2 transform hover:scale-105 active:scale-95"
              >
                <Zap size={24} />
                View Premium Plans
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white text-gray-700 border-2 border-gray-300 px-10 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition inline-flex items-center justify-center gap-2 transform hover:scale-105 active:scale-95"
              >
                <Home size={24} />
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (lessonError || !lesson) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50 animate-page-enter">
        <nav className="bg-white shadow-md p-4 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-xl">
                <BookOpen className="text-white" size={24} />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Activity Not Found</h1>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="hover:opacity-80 transition transform hover:scale-105 active:scale-95"
            >
              <img src="/assets/HomeButton.webp" alt="Home" className="h-10" />
            </button>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-4 py-8 sm:py-16">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-12 text-center">
            <div className="bg-gray-100 w-20 h-20 sm:w-32 sm:h-32 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <BookOpen className="text-gray-400" size={40} />
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Oops! Activity Not Found</h2>
            <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8">
              Activity {currentLessonNum} hasn't been created yet. Check back soon!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-10 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition inline-flex items-center justify-center gap-2 transform hover:scale-105 active:scale-95"
              >
                <Home size={24} />
                Back to Dashboard
              </button>
              <button
                onClick={() => {
                  // Find the nearest valid lesson
                  let nearestLesson = currentLessonNum - 1;
                  // Keep going back until we find a valid lesson or hit lesson 1
                  while (nearestLesson > 0 && !getLesson(nearestLesson, currentPhase)) {
                    nearestLesson--;
                  }
                  // If we found a valid lesson, go there, otherwise go to dashboard
                  if (nearestLesson > 0 && getLesson(nearestLesson, currentPhase)) {
                    setIsTransitioning(true);
                    setLessonError(false);
                    setTimeout(() => {
                      navigate(`/lesson/${nearestLesson}`);
                      window.scrollTo(0, 0);
                    }, 200);
                  } else {
                    navigate('/dashboard');
                  }
                }}
                className="bg-white text-gray-700 border-2 border-gray-300 px-10 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition inline-flex items-center justify-center gap-2 transform hover:scale-105 active:scale-95"
              >
                <ChevronLeft size={24} />
                Previous Activity
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Free trial — cold-lander context screen (shown once before the age selector)
  if (freeTrialMode && !trialAgeGroup && !trialIntroPassed) {
    return (
      <div key="trial-intro-screen" className={`min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50 flex flex-col screen-fade ${screenFading ? 'screen-fade-out' : 'screen-fade-in'} animate-page-enter`}>
        <nav className="bg-white shadow-md p-2.5 sm:p-4 sticky top-0 z-20 animate-slide-nav">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate(homeRoute, { state: { navSource: 'trial_intro_back_arrow' } })}
                className="flex items-center gap-1 text-purple-600 hover:text-purple-800 transition mr-1 sm:mr-2"
                title="Back to Home"
              >
                <ArrowLeft size={18} className="sm:hidden" /><ArrowLeft size={20} className="hidden sm:block" />
              </button>
              <img src="/assets/brlogo2.webp" alt="BrightReading" className="h-6 sm:h-8 w-auto object-contain" />
              <h1 className="text-base sm:text-xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">BrightReading</h1>
            </div>
          </div>
        </nav>

        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6">
          <div className="max-w-lg w-full">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 sm:p-8 animate-fade-up-d1">
              <div className="text-center mb-5 sm:mb-6">
                <div className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-bold mb-3">
                  <Heart size={12} className="text-purple-600" />Free activity inside
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight mb-2">
                  A 10-minute reading lesson — no screens for your child.
                </h2>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                  You read, you teach. Your child listens, plays, and answers back.
                </p>
              </div>

              <ul className="space-y-3 sm:space-y-3.5 mb-6 sm:mb-7">
                <li className="flex items-start gap-3">
                  <div className="bg-purple-100 p-1.5 rounded-lg flex-shrink-0 mt-0.5">
                    <Baby size={16} className="text-purple-600" />
                  </div>
                  <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
                    <span className="font-bold">Matched to your child's exact age</span> — from 1 to 5. Pick the right one in a tap.
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-amber-100 p-1.5 rounded-lg flex-shrink-0 mt-0.5">
                    <Zap size={16} className="text-amber-600" />
                  </div>
                  <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
                    <span className="font-bold">You'll be teaching in 2 minutes.</span> No setup, no signup, just start having fun.
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-pink-100 p-1.5 rounded-lg flex-shrink-0 mt-0.5">
                    <Heart size={16} className="text-pink-600" />
                  </div>
                  <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
                    <span className="font-bold">Back-and-forth with you</span> builds a stronger foundation than any app.
                  </p>
                </li>
              </ul>

              {/* Parent testimonial — highest-leverage trust signal at the cold-lander decision point */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-xl p-4 sm:p-5 mb-5 sm:mb-6">
                <div className="flex justify-center gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed italic text-center mb-2">
                  &ldquo;Love the app! My son wants to do multiple activities per with me and my wife! He really likes tracing the letters and saying them with me.&rdquo;
                </p>
                <p className="text-xs sm:text-sm font-black text-purple-600 text-center">&mdash; Jeff, Dad of 3 year old</p>
              </div>

              {/* Research credibility line — compact, expandable for the curious */}
              <button
                type="button"
                onClick={() => {
                  trackEvent('cta_click', { button: 'trial_intro_research_toggle' }, null);
                  navigate('/research', { state: { fromTrialIntro: true } });
                }}
                className="w-full flex items-center justify-center gap-2 mb-5 sm:mb-6 text-xs sm:text-sm text-gray-500 hover:text-purple-600 transition"
              >
                <BookOpen size={14} />
                Built on early-literacy &amp; phonics research
                <ArrowRight size={12} />
              </button>

              <button
                onClick={() => {
                  if (screenFading) return;
                  trackEvent('cta_click', { button: 'trial_intro_continue', destination: 'age_picker' }, null);
                  try { sessionStorage.setItem('trialIntroPassed', '1'); } catch {}
                  setScreenFading(true);
                  setTimeout(() => { setTrialIntroPassed(true); setScreenFading(false); }, 350);
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-black text-base sm:text-lg shadow-lg hover:shadow-xl transition transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              >
                Continue
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Free trial age selector screen
  if (freeTrialMode && !trialAgeGroup) {
    return (
      <div key="trial-age-screen" className={`min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50 flex flex-col screen-fade ${screenFading ? 'screen-fade-out' : 'screen-fade-in'} animate-page-enter`}>
        <nav className="bg-white shadow-md p-2.5 sm:p-4 sticky top-0 z-20 animate-slide-nav">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => {
                  if (screenFading) return;
                  trackEvent('cta_click', { button: 'trial_age_back', destination: 'intro' }, null);
                  try { sessionStorage.removeItem('trialIntroPassed'); } catch {}
                  setScreenFading(true);
                  setTimeout(() => { setTrialIntroPassed(false); setScreenFading(false); }, 350);
                }}
                className="flex items-center gap-1 text-purple-600 hover:text-purple-800 transition mr-1 sm:mr-2"
                title="Back"
              >
                <ArrowLeft size={18} className="sm:hidden" /><ArrowLeft size={20} className="hidden sm:block" />
              </button>
              <img src="/assets/brlogo2.webp" alt="BrightReading" className="h-6 sm:h-8 w-auto object-contain" />
              <h1 className="text-base sm:text-xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">BrightReading</h1>
            </div>
          </div>
        </nav>

        <div className="flex-1 flex flex-col items-center justify-center p-3 sm:p-6">
          <div className="max-w-lg w-full">

            {/* Unified white card — from "Here's what happens" through testimonial */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 sm:p-7 animate-fade-up-d2">

              {/* Reassurance pills — moved inside the card, larger and roomier */}
              <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5 mb-5 sm:mb-7 animate-fade-up-d1">
                <span className="inline-flex items-center gap-1.5 sm:gap-2 bg-gray-50 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold text-gray-700 shadow-sm border border-gray-200 whitespace-nowrap">
                  <Clock size={14} className="text-purple-500" />10 min
                </span>
                <span className="inline-flex items-center gap-1.5 sm:gap-2 bg-gray-50 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold text-gray-700 shadow-sm border border-gray-200 whitespace-nowrap">
                  <BookOpen size={14} className="text-pink-500" />Screen-free for child
                </span>
                <span className="inline-flex items-center gap-1.5 sm:gap-2 bg-gray-50 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold text-gray-700 shadow-sm border border-gray-200 whitespace-nowrap">
                  <Shield size={14} className="text-green-500" />No signup
                </span>
              </div>

              {/* How it works — micro-preview */}
              <p className="text-[11px] sm:text-xs font-black text-gray-400 uppercase tracking-wide mb-2 sm:mb-3 text-center">Here's what happens</p>
              <div className="flex items-start gap-3 sm:gap-4">
                {[
                  { num: '1', text: 'Pick your child\'s age', color: 'from-purple-500 to-pink-500' },
                  { num: '2', text: 'Read the script we give you', color: 'from-pink-500 to-rose-500' },
                  { num: '3', text: 'Do the activity together', color: 'from-rose-500 to-orange-500' },
                ].map((step) => (
                  <div key={step.num} className="flex-1 text-center flex flex-col items-center">
                    <div className={`bg-gradient-to-br ${step.color} text-white w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mb-1 sm:mb-1.5 font-black text-xs sm:text-sm shadow-md flex-shrink-0`}>{step.num}</div>
                    <p className="text-xs sm:text-sm text-gray-600 font-medium leading-tight">{step.text}</p>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 my-4 sm:my-6" />

              {/* Age selector */}
              <div className="text-center mb-2 sm:mb-4">
                <h3 className="text-base sm:text-xl font-black text-gray-900 animate-fade-up-d3">How old is your child?</h3>
              </div>

              <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
                {AGE_GROUPS.map((ag, i) => {
                  const isLoading = loadingAgeCard === ag.phase;
                  const isDisabled = loadingAgeCard !== null && loadingAgeCard !== ag.phase;
                  return (
                    <button
                      key={ag.phase}
                      disabled={isDisabled}
                      onClick={() => {
                        if (loadingAgeCard !== null) return;
                        setLoadingAgeCard(ag.phase);
                        trackEvent('cta_click', { button: 'trial_age_select', ageGroup: ag.label, phase: ag.phase }, null);
                        setScreenFading(true);
                        setTimeout(() => { setTrialAgeGroup(ag.phase); setScreenFading(false); }, 350);
                      }}
                      className={`age-card-btn bg-gradient-to-br ${ag.color} text-white rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition transform hover:scale-105 text-center cursor-pointer ${
                        i === 0 ? 'animate-fade-up-d3' : i === 1 ? 'animate-fade-up-d4' : i === 2 ? 'animate-fade-up-d5' : 'animate-fade-up-d6'
                      }${isDisabled ? ' opacity-50 pointer-events-none' : ''}${isLoading ? ' age-card-loading' : ''}`}
                    >
                      <div className="relative">
                        <img src={ag.icon} alt={`Ages ${ag.label}`} className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-1 sm:mb-2 object-contain${isLoading ? ' opacity-40' : ''}`} />
                        {isLoading && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="age-card-spinner" />
                          </div>
                        )}
                      </div>
                      <div className="text-xl sm:text-2xl font-black mb-0.5 sm:mb-1">{ag.label} years</div>
                      <div className="text-xs sm:text-sm text-white/80">{ag.subtitle}</div>
                    </button>
                  );
                })}
              </div>

              <p className="text-center text-xs sm:text-sm text-gray-400 mt-3 sm:mt-5 mb-4 sm:mb-6 animate-fade-up-d6">
                You'll be teaching in under 2 minutes.
              </p>

              {/* Trust badges */}
              <div className="flex justify-center gap-2 sm:gap-2 animate-fade-up-d6">
                <span className="inline-flex items-center gap-1 sm:gap-1.5 text-green-700 font-bold text-[11px] sm:text-xs bg-green-50 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-green-200">
                  <Shield size={11} className="text-green-600" />No ads
                </span>
                <span className="inline-flex items-center gap-1 sm:gap-1.5 text-green-700 font-bold text-[11px] sm:text-xs bg-green-50 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-green-200">
                  <Shield size={11} className="text-green-600" />No data sold
                </span>
                <span className="inline-flex items-center gap-1 sm:gap-1.5 text-green-700 font-bold text-[11px] sm:text-xs bg-green-50 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-green-200">
                  <Shield size={11} className="text-green-600" />Zero screen time
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mini footer */}
        <div className="px-4 py-4 text-center border-t border-gray-200/50">
          <p className="text-xs text-gray-400">
            <button onClick={() => navigate(homeRoute)} className="hover:text-gray-600 transition hover:underline">BrightReading</button>
            {' '}&middot;{' '}1,040 research-backed activities for ages 1-5
            {' '}&middot;{' '}<button onClick={() => navigate('/privacy')} className="hover:text-gray-600 transition hover:underline">Privacy</button>
            {' '}&middot;{' '}<button onClick={() => navigate('/terms')} className="hover:text-gray-600 transition hover:underline">Terms</button>
          </p>
        </div>
      </div>
    );
  }

  // Free trial feedback / completion full-page screen
  if (freeTrialMode && showFeedbackForm) {
    const ageInfo = AGE_GROUPS.find(a => a.phase === trialAgeGroup);
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50 flex flex-col animate-page-enter">
        <nav className="bg-white shadow-md p-4 sticky top-0 z-20 animate-slide-nav">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className={`bg-gradient-to-br ${ageInfo?.color} p-2 rounded-xl`}>
                <BookOpen className="text-white" size={24} />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Activity Complete</h1>
            </div>
          </div>
        </nav>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-lg w-full" key={feedbackSubmitted ? 'cta' : 'feedback'}>
            {!feedbackSubmitted ? (
              <>
                <div className="text-center mb-8">
                  <div className="animate-pop-in bg-gradient-to-br from-green-400 to-teal-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg">
                    <CheckCircle className="text-white" size={40} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2 animate-fade-up-d1">You Did It!</h2>
                  <p className="text-lg text-gray-600 animate-fade-up-d2">You just completed a free BrightReading activity.</p>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 text-left animate-fade-up-d3">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Heart className="text-pink-500" size={20} />
                    We'd love your feedback!
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    What did you think? Any feedback at all is appreciated — it helps us build something better for families like yours.
                  </p>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Share your thoughts, suggestions, or anything you'd like us to know..."
                    className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none text-sm resize-none h-32 transition mb-3"
                  />
                  <input
                    type="email"
                    value={feedbackEmail}
                    onChange={(e) => setFeedbackEmail(e.target.value)}
                    placeholder="Your email (optional — we'll send you free tips)"
                    className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none text-sm transition"
                  />
                </div>

                <button
                  onClick={handleSubmitFeedback}
                  disabled={feedbackSubmitting || (!feedbackText.trim() && !feedbackEmail.trim())}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition mb-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed animate-fade-up-d4"
                >
                  <Send size={20} />
                  {feedbackSubmitting ? 'Sending...' : 'Send Feedback'}
                </button>

                <button
                  onClick={() => setFeedbackSubmitted(true)}
                  className="w-full bg-gray-100 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-200 transition text-sm animate-fade-up-d4"
                >
                  Skip
                </button>
              </>
            ) : (
              <div className="text-center">
                <div className="animate-pop-in bg-gradient-to-br from-green-400 to-emerald-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg">
                  <CheckCircle className="text-white" size={40} />
                </div>
                <p className="text-gray-600 text-lg mb-2 animate-fade-up-d1">{ageInfo?.completionLine}</p>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 animate-fade-up-d2">Ready for the next one?</h2>

                <button
                  onClick={() => {
                    try { sessionStorage.setItem('trialCompleted', JSON.stringify({ phase: trialAgeGroup, lessonId: 1, ageGroup: ageInfo?.label })); } catch {}
                    navigate('/login', { state: { fromTrial: true } });
                  }}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition mb-3 flex items-center justify-center gap-2 animate-fade-up-d3"
                >
                  Get 9 More Free Activities
                  <ArrowRight size={20} />
                </button>

                <button
                  onClick={() => navigate(homeRoute, { state: { navSource: 'trial_complete_back_home' } })}
                  className="w-full bg-gray-100 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-200 transition text-sm animate-fade-up-d4"
                >
                  Back to Home
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div key="lesson-main-screen" className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50 animate-page-enter">
      {/* Navigation */}
      <nav className="bg-white shadow-md p-3 sm:p-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {!user && (
              <button
                onClick={() => {
                  if (freeTrialMode) {
                    try { sessionStorage.removeItem('trialAgeGroup'); } catch {}
                    setTrialAgeGroup(null);
                    setLoadingAgeCard(null);
                    setScreenFading(false);
                    setIsLessonCompleted(false);
                    setCurrentStep(0);
                    setCompletedSteps([]);
                    setLessonError(false);
                  } else {
                    navigate(homeRoute, { state: { navSource: 'lesson_back_arrow' } });
                  }
                }}
                className="flex items-center gap-1 text-purple-600 hover:text-purple-800 transition mr-1 sm:mr-2 flex-shrink-0"
                title={freeTrialMode ? "Back to age selector" : "Back to Home"}
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div className={`bg-gradient-to-br ${currentPhaseInfo?.color} p-1.5 sm:p-2 rounded-lg sm:rounded-xl flex-shrink-0`}>
              <BookOpen className="text-white" size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <h1 className="text-sm sm:text-lg md:text-xl font-bold text-gray-900 truncate">Week {weekNum}, Activity {lessonInWeek}</h1>
                <span className={`text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-gradient-to-r ${currentPhaseInfo?.color} text-white flex-shrink-0`}>
                  P{currentPhase}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 truncate">{lesson.theme}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {!freeTrialMode && (
<button
              onClick={() => {
                setViewingWeek(weekNum);
                setShowWeekView(true);
              }}
              className="hover:opacity-80 transition"
            >
              <img src="/assets/Weeks.webp" alt="Weeks" className="h-10" />
            </button>
            )}
{!freeTrialMode && (
            <button
              onClick={() => {
                setIsTransitioning(true);
                setTimeout(() => {
                  navigate(user ? '/dashboard' : '/');
                  window.scrollTo(0, 0);
                }, 200);
              }}
              className="hover:opacity-80 transition"
            >
              <img src="/assets/HomeButton.webp" alt="Home" className="h-10" />
            </button>
            )}
          </div>
        </div>
      </nav>

      {/* Progress & Status Bar */}
      <div className="bg-white border-b-2 border-gray-200 sticky top-[56px] sm:top-[72px] z-10">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Phase Progress Banner */}
          {freeTrialMode ? (
            <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 mb-3 sm:mb-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2 sm:gap-3">
                <Play size={20} className="flex-shrink-0 sm:w-6 sm:h-6" />
                <div>
                  <p className="font-semibold text-sm sm:text-base">Free Activity</p>
                  <p className="text-[10px] sm:text-xs text-white/80">No signup needed — just explore!</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className={`bg-gradient-to-r ${currentPhaseInfo?.color} rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 mb-3 sm:mb-4 flex items-center justify-between text-white`}>
                <div className="flex items-center gap-2 sm:gap-3">
                  <Trophy size={20} className="flex-shrink-0 sm:w-6 sm:h-6" />
                  <div>
                    <p className="font-semibold text-sm sm:text-base">
                      {currentPhaseInfo?.name}: {completedLessonsCount}/260
                    </p>
                    <p className="text-[10px] sm:text-xs text-white/80">
                      {currentPhaseInfo?.subtitle} • {Math.round((completedLessonsCount / 260) * 100)}% complete
                    </p>
                  </div>
                </div>
              </div>

              {/* Completion Status */}
              {alreadyCompleted && (
                <div className="bg-green-50 border-2 border-green-300 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                  <p className="text-green-800 font-semibold text-xs sm:text-sm md:text-base">
                    ✓ Completed! You can review this activity anytime.
                  </p>
                </div>
              )}

              {/* Free Trial Banner */}
              {!isPremium && (hasFreeLessonsRemaining || alreadyCompleted) && (
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 mb-3 sm:mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3 text-white">
                    <Zap size={20} className="flex-shrink-0 sm:w-6 sm:h-6" />
                    <p className="font-semibold text-xs sm:text-sm md:text-base">
                      Free: {totalCompletedAcrossAllPhases}/10 done • {10 - totalCompletedAcrossAllPhases} left
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Progress Bar */}
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <span className="text-sm sm:text-base font-bold text-gray-900">
              Step {currentStep + 1} of {lesson.steps.length}
            </span>
            <span className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">
              {currentStepData?.duration || '5-10 min'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className={`bg-gradient-to-r ${currentPhaseInfo?.color} h-3 rounded-full transition-all duration-500`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div ref={contentRef} className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8 pb-safe">
        <div className={`transition-opacity duration-200 ease-in-out ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>

          {currentStep === 0 && (
            <LessonBanner
              lessonId={lesson.id}
              bannerText={lesson.banner}
              phaseKey={lesson.phase}
            />
          )}
          <StepCard
            stepData={currentStepData}
            stepIndex={currentStep}
            totalSteps={lesson.steps.length}
            phaseColor={currentPhaseInfo?.color}
            isLastStep={currentStep === lesson.steps.length - 1}
            onNext={handleNext}
            onBack={handleBack}
            onComplete={handleCompleteLesson}
          />
        </div>
      </div>

      {/* Review Prompt Modal — shown once after 3rd activity */}
      {showWeeklyRating && createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 sm:p-4 min-h-[100dvh]">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 text-center animate-scale-in">
            {!weeklyRatingSubmitted ? (
              <>
                <div className="mb-4 sm:mb-6">
                  <div className="bg-gradient-to-br from-yellow-400 to-orange-500 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                    <Star className="text-white" size={32} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Week {weekNum} complete!</h2>
                  <p className="text-sm sm:text-base text-gray-500">How did your child do this week? (1–5 stars)</p>
                </div>

                <div className="flex justify-center gap-1.5 sm:gap-2 mb-4 sm:mb-5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setWeeklyRatingValue(star)}
                      onMouseEnter={() => setWeeklyRatingHover(star)}
                      onMouseLeave={() => setWeeklyRatingHover(0)}
                      className="transition transform hover:scale-110"
                      aria-label={`${star} star${star > 1 ? 's' : ''}`}
                    >
                      <Star
                        size={36}
                        className={`sm:w-10 sm:h-10 transition ${
                          star <= (weeklyRatingHover || weeklyRatingValue)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>

                {weeklyRatingValue > 0 && (
                  <div className="mb-4 sm:mb-5 animate-fade-up-d1">
                    <textarea
                      value={weeklyRatingNote}
                      onChange={(e) => setWeeklyRatingNote(e.target.value)}
                      placeholder="Anything you want to remember? (optional)"
                      className="w-full border border-gray-200 rounded-xl p-3 text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-transparent"
                      rows={3}
                      maxLength={1000}
                    />
                  </div>
                )}

                <div className="flex flex-col gap-2 sm:gap-3">
                  <button
                    onClick={handleSubmitWeeklyRating}
                    disabled={!weeklyRatingValue || weeklyRatingSubmitting}
                    className={`w-full py-3 rounded-xl font-bold text-base transition ${
                      weeklyRatingValue
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:shadow-lg'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {weeklyRatingSubmitting ? 'Saving...' : 'Save Rating'}
                  </button>
                  <button
                    onClick={handleCloseWeeklyRating}
                    className="text-sm text-gray-400 hover:text-gray-600 transition py-1"
                  >
                    Skip
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-5 sm:mb-6">
                  <div className="bg-gradient-to-br from-green-400 to-emerald-500 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                    <Heart className="text-white" size={32} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Saved!</h2>
                  <p className="text-sm sm:text-base text-gray-500">We’ll use this to track your child’s progress over time.</p>
                </div>
                <button
                  onClick={handleCloseWeeklyRating}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-3 rounded-xl font-bold text-base hover:shadow-lg transition"
                >
                  Continue
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {showReviewPrompt && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 sm:p-4 min-h-[100dvh]">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 text-center animate-scale-in">
            {!reviewSubmitted ? (
              <>
                <div className="mb-4 sm:mb-6">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-500 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                    <Star className="text-white" size={32} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Enjoying BrightReading?</h2>
                  <p className="text-sm sm:text-base text-gray-500">You've completed 3 activities! We'd love your feedback.</p>
                </div>

                {/* Star rating */}
                <div className="flex justify-center gap-1.5 sm:gap-2 mb-4 sm:mb-5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      onMouseEnter={() => setReviewHover(star)}
                      onMouseLeave={() => setReviewHover(0)}
                      className="transition transform hover:scale-110"
                    >
                      <Star
                        size={36}
                        className={`sm:w-10 sm:h-10 transition ${
                          star <= (reviewHover || reviewRating)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>

                {/* Optional message — only show after rating */}
                {reviewRating > 0 && (
                  <div className="mb-4 sm:mb-5 animate-fade-up-d1">
                    <textarea
                      value={reviewMessage}
                      onChange={(e) => setReviewMessage(e.target.value)}
                      placeholder="Tell us more (optional)"
                      className="w-full border border-gray-200 rounded-xl p-3 text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                      rows={3}
                      maxLength={1000}
                    />
                  </div>
                )}

                <div className="flex flex-col gap-2 sm:gap-3">
                  <button
                    onClick={handleSubmitReview}
                    disabled={!reviewRating || reviewSubmitting}
                    className={`w-full py-3 rounded-xl font-bold text-base transition ${
                      reviewRating
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                  <button
                    onClick={handleSkipReview}
                    className="text-sm text-gray-400 hover:text-gray-600 transition py-1"
                  >
                    Skip
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-5 sm:mb-6">
                  <div className="bg-gradient-to-br from-green-400 to-emerald-500 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                    <Heart className="text-white" size={32} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Thank you!</h2>
                  <p className="text-sm sm:text-base text-gray-500">Your feedback helps us improve for every family.</p>
                </div>
                <button
                  onClick={handleReviewDone}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-bold text-base hover:shadow-lg transition"
                >
                  Continue
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 sm:p-4 min-h-[100dvh]">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 md:p-10 text-center animate-scale-in">
            <div className="mb-5 sm:mb-8">
              <div className="bg-gradient-to-br from-yellow-400 to-orange-500 w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <Trophy className="text-white" size={40} />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">Amazing Work!</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600">You've completed Activity {currentLessonNum} in {currentPhaseInfo?.name}</p>
            </div>

            <div className={`bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 mb-5 sm:mb-8 border-2 ${currentPhaseInfo?.color.replace('from-', 'border-').replace(' to-', '')}`}>
              <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 text-center">
                <div>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-purple-600 mb-1 sm:mb-2">{completedLessonsCount}</p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 font-medium">Phase {currentPhase}</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 mb-1 sm:mb-2">{Math.round((completedLessonsCount / 260) * 100)}%</p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 font-medium">Complete</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl md:text-4xl mb-1 sm:mb-2">⭐</p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 font-medium">Achievement</p>
                </div>
              </div>
            </div>

            <p className="text-gray-700 mb-5 sm:mb-8 text-sm sm:text-base md:text-lg">
              Your child is building a strong foundation for reading success!
            </p>

            <button
              onClick={handleFinish}
              className={`w-full bg-gradient-to-r ${currentPhaseInfo?.color} text-white py-3 sm:py-4 md:py-5 rounded-xl font-bold text-base sm:text-lg md:text-xl hover:shadow-xl transition`}
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      )}

      {/* Paywall Modal */}
      {showPaywall && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 sm:p-4 min-h-[100dvh]">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 md:p-10 text-center animate-scale-in max-h-[95dvh] overflow-y-auto">
            <div className="mb-5 sm:mb-8">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <Zap className="text-white" size={40} />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">You Did It!</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600">You've completed all 10 free trial activities! 🎉</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 text-left">
              <p className="text-gray-900 font-bold mb-3 sm:mb-5 text-base sm:text-lg">You've experienced:</p>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex gap-2 sm:gap-3 text-gray-800 text-sm sm:text-base">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
                  <span>Research-backed activity design</span>
                </li>
                <li className="flex gap-2 sm:gap-3 text-gray-800 text-sm sm:text-base">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
                  <span>Parent guidance & activities</span>
                </li>
                <li className="flex gap-2 sm:gap-3 text-gray-800 text-sm sm:text-base">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
                  <span>Proven literacy techniques</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-5 sm:mb-8 text-left">
              <p className="text-gray-800 text-sm sm:text-base">
                <span className="font-bold text-blue-900">250+ more activities</span> await with Premium — covering all 4 developmental phases with complete activity book progression.
              </p>
            </div>

            <button
              onClick={() => {
                setShowPaywall(false);
                navigate('/upgrade');
              }}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 sm:py-4 md:py-5 rounded-xl font-bold text-base sm:text-lg md:text-xl hover:shadow-xl transition mb-3 sm:mb-4"
            >
              Upgrade to Premium
            </button>

            <button
              onClick={() => {
                setShowPaywall(false);
                navigate('/dashboard');
              }}
              className="w-full bg-gray-200 text-gray-800 py-2.5 sm:py-3 md:py-4 rounded-xl font-semibold hover:bg-gray-300 transition text-sm sm:text-base"
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Free trial feedback is now a full-page screen (rendered as early return above) */}

{/* Week View Modal */}
      {showWeekView && createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 min-h-[100dvh] animate-fade-in" onClick={() => {
          setShowWeekView(false);
          setViewingWeek(null);
        }}>
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[95dvh] sm:max-h-[90vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>            <div className={`sticky top-0 bg-gradient-to-r ${currentPhaseInfo?.color} text-white p-4 sm:p-6 rounded-t-2xl sm:rounded-t-3xl z-10`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <Calendar size={24} className="sm:w-8 sm:h-8 flex-shrink-0" />
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold">Week {displayWeek}</h2>
                    <p className="text-white/90 text-sm">{currentPhaseInfo?.name} • 5 activities</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowWeekView(false);
                    setViewingWeek(null);
                  }}
                  className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg transition flex-shrink-0"
                >
                  <X className="text-white" size={24} />
                </button>
              </div>
            </div>

            <div className="p-3 sm:p-6 space-y-2 sm:space-y-3">
              {displayWeekLessons.map((lesson) => (
                <div
                  key={lesson.id}
                  className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-5 rounded-xl border-2 transition ${
                    lesson.id === currentLessonNum
                      ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-300'
                      : lesson.completed
                      ? 'bg-green-50 border-green-300'
                      : lesson.canAccess
                      ? 'bg-white border-gray-200 hover:border-blue-300'
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div
                    className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center font-bold text-base sm:text-xl flex-shrink-0 ${
                      lesson.completed
                        ? 'bg-green-500 text-white'
                        : lesson.canAccess
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-300 text-gray-500'
                    }`}
                  >
                    {lesson.completed ? <CheckCircle size={28} /> : lesson.canAccess ? lesson.number : <Lock size={20} />}
                  </div>
<div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg">
                      {getLesson(lesson.id, currentPhase)?.title || `Activity ${lesson.id}`}
                      {lesson.id === currentLessonNum && (
                        <span className="ml-2 text-sm bg-blue-500 text-white px-2 py-1 rounded-full">
                          Current
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {lesson.completed
                        ? '✓ Completed'
                        : lesson.canAccess
                        ? 'Ready to start'
                        : 'Locked'}
                    </p>
                  </div>
{lesson.canAccess && (
                    <button
                      onClick={() => {
                        if (lesson.id !== currentLessonNum) {
                          setIsTransitioning(true);
                          setTimeout(() => {
                            setShowWeekView(false);
                            setViewingWeek(null);
                            navigate(`/lesson/${lesson.id}`);
                          }, 200);
                        }
                      }}
                      className={`px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2 ${
                        lesson.completed
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : `bg-gradient-to-r ${currentPhaseInfo?.color} text-white hover:shadow-lg`
                      }`}
                    >
                      {lesson.id === currentLessonNum ? 'Current' : lesson.completed ? 'Review' : 'Start'}
                      {lesson.id !== currentLessonNum && <Play size={16} />}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-gray-50 p-6 rounded-b-3xl border-t">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  {displayWeekLessons.filter(l => l.completed).length} of 5 completed
                </span>
                <div className="flex gap-2">
  {displayWeek > currentQuarterWeeks[0].week && (
    <button
      onClick={() => setViewingWeek(displayWeek - 1)}
      className="text-blue-600 hover:text-blue-700 font-semibold"
    >
      ← Previous Week
    </button>
  )}
  {displayWeek < currentQuarterWeeks[currentQuarterWeeks.length - 1].week && currentQuarterWeeks.find(w => w.week === displayWeek + 1) && (
    <button
      onClick={() => setViewingWeek(displayWeek + 1)}
      className="text-blue-600 hover:text-blue-700 font-semibold"
    >
      Next Week →
    </button>
  )}
</div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

<style>{`
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in { animation: scale-in 0.3s ease-out; }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }

        @keyframes fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: fade-up 0.5s ease-out both; }

        @keyframes fade-up-delay-1 { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-up-d1 { animation: fade-up-delay-1 0.5s ease-out 0.1s both; }
        .animate-fade-up-d2 { animation: fade-up-delay-1 0.5s ease-out 0.2s both; }
        .animate-fade-up-d3 { animation: fade-up-delay-1 0.5s ease-out 0.3s both; }
        .animate-fade-up-d4 { animation: fade-up-delay-1 0.5s ease-out 0.4s both; }
        .animate-fade-up-d5 { animation: fade-up-delay-1 0.5s ease-out 0.5s both; }
        .animate-fade-up-d6 { animation: fade-up-delay-1 0.5s ease-out 0.6s both; }

        @keyframes pop-in {
          0% { opacity: 0; transform: scale(0.8); }
          70% { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-pop-in { animation: pop-in 0.4s ease-out both; }

        @keyframes slide-nav {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-nav { animation: slide-nav 0.3s ease-out both; }
      `}</style>
    </div>
  );
};

export default LessonPage;